import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    EventEmitter,
    inject,
    Input,
    Output,
} from '@angular/core';
import { translate, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { selectTranslate, StringUtil } from '@libs/vc-core-lib';
import { ChartWrapperComponent } from '@libs/vc-shared-ui-lib';
import * as Highcharts from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { isEmpty } from 'lodash-es';
import { DateRange, SelectComponent } from 'vcp-common-ui-lib';
import { ITimelineItemModel } from '../../model/timeline-item.model';
import { AssessmentTooltipFormatter } from '../assessment-tooltip-formatter';

const DATE_AXIS_LABEL_FORMAT = '{value:%b %d}';
const MONTH_AXIS_LABEL_FORMAT = '{value:%b %m}';

@Component({
    selector: 'vc-timeline-chart',
    templateUrl: './timeline-chart.component.html',
    styleUrls: ['./timeline-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslocoPipe, ChartWrapperComponent, SelectComponent, HighchartsChartComponent],
})
export class TimelineChartComponent {
    private readonly _transloco: TranslocoService = inject(TranslocoService);
    private readonly _destroyRef = inject(DestroyRef);

    private readonly _changeDetectionRef: ChangeDetectorRef = inject(ChangeDetectorRef);

    times = Object.values(TimelineOptions);
    selectedTime: TimelineOptions | null = TimelineOptions.WEEK;
    chartOptions!: Highcharts.Options;
    highcharts = Highcharts;
    private chartInstance: Highcharts.Chart | null = null;

    chartCallback: Highcharts.ChartCallbackFunction = (chart: Highcharts.Chart) => {
        this.chartInstance = chart;
    };

    TICK_INTERVAL: Partial<Record<TimelineOptions, number>> = {
        [TimelineOptions.WEEK]: 24 * 7 * 3600 * 1000,
        [TimelineOptions.MONTH]: 31 * 24 * 3600 * 1000,
        [TimelineOptions.THREE_MONTHS]: 30 * 3 * 24 * 3600 * 1000,
        [TimelineOptions.YEAR]: 365 * 24 * 3600 * 1000,
    };

    LABELS_FORMAT: Partial<Record<TimelineOptions, Highcharts.XAxisLabelsOptions>> = {
        [TimelineOptions.WEEK]: { format: DATE_AXIS_LABEL_FORMAT },
        [TimelineOptions.MONTH]: { format: DATE_AXIS_LABEL_FORMAT },
        [TimelineOptions.THREE_MONTHS]: { format: MONTH_AXIS_LABEL_FORMAT },
        [TimelineOptions.YEAR]: { format: MONTH_AXIS_LABEL_FORMAT },
    };

    @Input()
    set items(value: ITimelineItemModel[]) {
        this.initChart(value);
        this._items = value;
    }
    get items(): ITimelineItemModel[] {
        return this._items;
    }
    private _items!: ITimelineItemModel[];

    @Input()
    tooltipOptions!: Highcharts.TooltipOptions;

    @Input()
    loading: boolean = false;

    @Input()
    showTimeIntervalSelector: boolean = true;

    @Output()
    timeIntervalChange = new EventEmitter<number>();

    constructor() {
        selectTranslate(
            this._transloco,
            () => {
                this.setChartOptions();
                this._changeDetectionRef.markForCheck();
                this.chartInstance?.update(this.chartOptions, true, true);
            },
            this._destroyRef
        );
    }

    private startTimelineItems!: IChartTimelineItem[];
    private milestoneTimelineItems!: IChartTimelineItem[];
    private dueTimelineItems!: IChartTimelineItem[];
    private meetingTimelineItems!: IChartTimelineItem[];
    private tickPositions!: number[];
    private dateRange!: DateRange | null;

    timeIntervalItemRenderer = (option: TimelineOptions): string => {
        switch (option) {
            case TimelineOptions.WEEK:
                return `1 ${translate('CORE.WEEK_LOWERCASE')}`;
            case TimelineOptions.MONTH:
                return `1 ${translate('CORE.MONTH_LOWERCASE')}`;
            case TimelineOptions.THREE_MONTHS:
                return `3 ${translate('CORE.MONTHS_LOWERCASE')}`;
            case TimelineOptions.YEAR:
                return `1 ${translate('CORE.YEAR_LOWERCASE')}`;
            default:
                return option;
        }
    };

    private initChart(items: ITimelineItemModel[]): void {
        // Reset all legend items to visible before rendering new data
        if (this.chartInstance) {
            this.chartInstance.series.forEach((series) => {
                if (!series.visible) {
                    series.show();
                }
            });
            this._changeDetectionRef.markForCheck();
        }

        this.startTimelineItems = [];
        this.dueTimelineItems = [];
        this.milestoneTimelineItems = [];
        this.meetingTimelineItems = [];
        this.dateRange = null;

        if (!isEmpty(items)) {
            let yPoint: number = 2;
            let repeatedDate: string | null = null;
            const sortedItems = [...items].sort((firstTimelineItem, secondTimelineItem) => {
                const firstItemDate = new Date(firstTimelineItem.date);
                const secondItemDate = new Date(secondTimelineItem.date);
                return firstItemDate.getTime() - secondItemDate.getTime();
            });

            this.calculateTickPositions(
                new Date(sortedItems[0].date),
                new Date(sortedItems[sortedItems.length - 1].date)
            );
            sortedItems.forEach((timelineItem) => {
                if (repeatedDate === timelineItem.date) {
                    yPoint++;
                } else {
                    yPoint = 2;
                    repeatedDate = timelineItem.date;
                }
                const chartTimelineItem: IChartTimelineItem = {
                    x: Date.parse(timelineItem.date),
                    y: yPoint,
                    item: timelineItem,
                    slip:
                        timelineItem.originalDate == null || StringUtil.isBlank(timelineItem.originalDate)
                            ? 0
                            : this.getDateDiffInDays(timelineItem.originalDate, timelineItem.date),
                };
                switch (timelineItem.type) {
                    case SeriesType.START:
                        this.startTimelineItems.push(chartTimelineItem);
                        break;
                    case SeriesType.MILESTONE:
                        this.milestoneTimelineItems.push(chartTimelineItem);
                        break;
                    case SeriesType.DUE:
                        this.dueTimelineItems.push(chartTimelineItem);
                        break;
                    case SeriesType.MEETING:
                        this.meetingTimelineItems.push(chartTimelineItem);
                        break;
                }
            });
        }
        this.setChartOptions();
    }

    private calculateTickPositions(startDate: Date, endDate: Date): void {
        this.tickPositions = [];
        const diffInDays: number = this.getDateDiffInDays(startDate.toDateString(), endDate.toDateString());
        if (diffInDays > 365) {
            startDate.setFullYear(startDate.getFullYear() - 1, 0, 1);
            endDate.setFullYear(endDate.getFullYear() + 1, 0, 1);
            this.dateRange = { startDate: new Date(startDate), endDate: new Date(endDate) };

            while (startDate <= endDate) {
                this.tickPositions.push(startDate.getTime());
                startDate.setFullYear(startDate.getFullYear() + 1);
            }
        } else if (diffInDays > 15) {
            startDate.setDate(1);
            startDate.setMonth(startDate.getMonth() - 1);
            endDate.setDate(1);
            endDate.setMonth(endDate.getMonth() + 1);
            this.dateRange = { startDate: new Date(startDate), endDate: new Date(endDate) };
            while (startDate <= endDate) {
                this.tickPositions.push(startDate.getTime());
                startDate.setMonth(startDate.getMonth() + 1);
            }
        } else {
            startDate.setDate(startDate.getDate() - 1);
            endDate.setDate(endDate.getDate() + 1);
            this.dateRange = { startDate: new Date(startDate), endDate: new Date(endDate) };
            while (startDate <= endDate) {
                this.tickPositions.push(startDate.getTime());
                startDate.setDate(startDate.getDate() + 1);
            }
        }
    }

    private getDateDiffInDays(startDate: string, endDate: string): number {
        return (Date.parse(endDate) - Date.parse(startDate)) / (1000 * 3600 * 24);
    }

    private setChartOptions(): void {
        this.chartOptions = {
            title: { text: '' },
            chart: {
                type: 'scatter',
                borderWidth: 0,
                spacingTop: 20,
                spacingBottom: 16,
            },
            tooltip: this.tooltipOptions ?? {
                formatter: AssessmentTooltipFormatter.tooltipFormatter,
            },
            yAxis: { labels: { enabled: false }, title: { text: undefined } },
            series: [
                {
                    name: translate('TIMELINE.CHART.ITEM_TYPE_START'),
                    type: 'scatter',
                    data: this.startTimelineItems,
                    marker: { symbol: MarkerType.START, radius: 8 },
                    color: 'var(--primary-800)',
                },
                {
                    name: translate('TIMELINE.CHART.ITEM_TYPE_MILESTONE'),
                    type: 'scatter',
                    data: this.milestoneTimelineItems,
                    marker: { symbol: MarkerType.MILESTONE, radius: 8 },
                    color: 'var(--primary-400)',
                },
                {
                    name: translate('TIMELINE.CHART.ITEM_TYPE_DUE'),
                    type: 'scatter',
                    data: this.dueTimelineItems,
                    marker: { symbol: MarkerType.DUE, radius: 8 },
                    color: 'var(--primary-200)',
                },
                {
                    name: translate('TIMELINE.CHART.ITEM_TYPE_MEETING'),
                    type: 'scatter',
                    data: this.meetingTimelineItems,
                    marker: { symbol: MarkerType.MEETING, radius: 8 },
                    color: 'var(--primary-600)',
                },
            ],
            credits: { enabled: false },
        };

        if (this.dateRange) {
            this.chartOptions.xAxis = {
                gridLineWidth: 1,
                type: 'datetime',
                min: this.dateRange.startDate?.getTime(),
                max: this.dateRange.endDate?.getTime(),
                tickPositions: this.tickPositions,
                tickInterval: this.selectedTime ? this.TICK_INTERVAL[this.selectedTime] : 1,
                labels: this.selectedTime ? this.LABELS_FORMAT[this.selectedTime] : { format: '' },
            };
        } else {
            this.chartOptions.xAxis = {
                gridLineWidth: 1,
                type: 'datetime',
            };
        }
    }

    onTimeIntervalChange(option: TimelineOptions | null): void {
        switch (option) {
            case TimelineOptions.WEEK:
                this.timeIntervalChange.emit(7);
                break;
            case TimelineOptions.MONTH:
                this.timeIntervalChange.emit(31);
                break;
            case TimelineOptions.THREE_MONTHS:
                this.timeIntervalChange.emit(90);
                break;
            case TimelineOptions.YEAR:
                this.timeIntervalChange.emit(365);
                break;
            default:
                break;
        }
    }
}
interface IChartTimelineItem {
    /**
     * x represents x-coordinate for chart.
     */
    x: number;
    /**
     * y represents y-coordinate for chart.
     */
    y: number;
    item: ITimelineItemModel;
    slip: number;
}

enum TimelineOptions {
    WEEK = '1 week',
    MONTH = '1 month',
    THREE_MONTHS = '3 months',
    YEAR = '1 year',
}

enum SeriesType {
    START = 'START',
    MILESTONE = 'MILESTONE',
    DUE = 'DUE',
    MEETING = 'MEETING',
}

enum MarkerType {
    START = 'square',
    MILESTONE = 'triangle',
    DUE = 'circle',
    MEETING = 'diamond',
}
